import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { editRestaurant } from '../../../api/restaurant';
import { useRestaurantContext } from '../../../contexts/RestaurantContext';
import useRestaurantWizardInitialValues from '../RestaurantWizard/useRestaurantWizardInitialValues';
import { RestaurantWizardFormValues, RestaurantWizardSchema } from '../RestaurantWizard/restaurantWizardSchema';
import { buildRestaurantHours } from '../../../utils/time';
import { DaysAndHoursPicker, TextArea } from '../../../components/common/Form';
import ProfileSectionPage from '../../../components/ProfileSectionPage/ProfileSectionPage';

import './styles/_location-profile.scss';
import LocationProfileActions from './LocationProfileActions';

const LocationHoursPage = () => {
  const { restaurantID } = useParams<{ restaurantID: string }>();
  const { currentRestaurant, isLoading, updateRestaurant } = useRestaurantContext();

  const initialValues = useRestaurantWizardInitialValues(true, currentRestaurant);

  const formik = useFormik<RestaurantWizardFormValues>({
    initialValues,
    validationSchema: RestaurantWizardSchema.pick(['restaurantHours', 'availabilityNotes']),
    enableReinitialize: true,
    validateOnBlur: true,

    onSubmit: async (values, { setSubmitting }) => {
      try {
        const restaurantHours = buildRestaurantHours(values.restaurantHours);

        await editRestaurant({
          restaurantHours,
          availabilityNotes: values.availabilityNotes
        });

        if (currentRestaurant) {
          updateRestaurant({
            ...currentRestaurant,
            restaurantHours,
            availabilityNotes: values.availabilityNotes
          });
        }

        toast.success('Hours saved successfully.');
      } catch {
        toast.error('Failed to save hours.');
      } finally {
        setSubmitting(false);
      }
    }
  });

  const handleSave = async () => {
    const errors = await formik.validateForm();

    formik.setTouched({
      restaurantHours: true,
      availabilityNotes: true
    });

    if (Object.keys(errors).length > 0) {
      return;
    }

    await formik.submitForm();
  };

  if (isLoading || !currentRestaurant?.restaurantID) {
    return null;
  }

  if (restaurantID && Number(restaurantID) !== currentRestaurant.restaurantID) {
    return null;
  }

  return (
    <ProfileSectionPage title="HOURS">
      <form className="location-profile-content" onSubmit={formik.handleSubmit}>
        <div className="location-hours-page">
          <DaysAndHoursPicker
            value={formik.values.restaurantHours}
            touched={Boolean(formik.touched.restaurantHours)}
            error={formik.errors.restaurantHours ? 'Value is required.' : undefined}
            name="restaurantHours"
            formik={formik}
            className="location-hours-day-picker"
          />

          <TextArea
            name="availabilityNotes"
            label={
              <span>
                NOTES <i>(Optional)</i>
              </span>
            }
            className="location-hours-availability-notes-textarea"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.availabilityNotes}
            touched={formik.touched.availabilityNotes}
            error={formik.errors.availabilityNotes}
          />

          <LocationProfileActions submit onSave={handleSave} />
        </div>
      </form>
    </ProfileSectionPage>
  );
};

export default LocationHoursPage;
